// MF expose "./theme" — INTERIM home of the design-system contract. Moves
// verbatim to the waypoint MFE (deferred); consumers will only swap the remote
// name.
//
// WaypointPlugin installs the DS layer into the CONSUMER's app. Its primevue
// imports resolve through the share scope — every shared entry is a
// strictVersion singleton, so there is exactly one satisfying copy and it is
// the one the consumer's pages use. Safe in hosted AND standalone modes.
import type { App, Plugin } from "vue";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";
import { WaypointTheme } from "./theme";

export interface WaypointPluginOptions {
  /**
   * DEFAULT OFF (legacy transition): most hosts don't declare CSS layers, and
   * layered PrimeVue styles would lose to their unlayered CSS. This host and
   * remotes running under it pass `true` (or a custom {name, order}).
   */
  cssLayer?: boolean | { name: string; order: string };
  darkModeSelector?: string;
}

export const primeVueThemeOptions = {
  darkModeSelector: ".app-dark-mode",
  cssLayer: { name: "primevue", order: "primevue, app" },
} as const;

export const WaypointPlugin: Plugin<[WaypointPluginOptions?]> = {
  install(app: App, options: WaypointPluginOptions = {}) {
    const { cssLayer = false, darkModeSelector = primeVueThemeOptions.darkModeSelector } = options;
    const resolvedLayer = cssLayer === true ? primeVueThemeOptions.cssLayer : cssLayer;
    app.use(PrimeVue, {
      theme: { preset: WaypointTheme, options: { darkModeSelector, cssLayer: resolvedLayer } },
    });
    app.use(ToastService);
    app.use(ConfirmationService);
    app.directive("tooltip", Tooltip);
  },
};

// Escape hatch: raw preset data for consumers that must assemble manually.
export { WaypointTheme } from "./theme";
